import React from 'react'
import PropTypes from 'prop-types'
import { equals } from 'ramda'

import { useDispatch } from 'react-redux'
import { setOutputField } from 'actions/feature/outputs'

import { useSelector } from 'hooks/redux'
import { getPwmOutputProps } from 'selectors/feature/outputs'

import { PWM_MODES, PWM_MODES_MAPPING } from 'constants/featureOutputs'
import { pwmModeTitle } from 'i18n/displayConstants/featureOutputs'
import strings from 'utils/strings'

import { schemaProp } from 'schemas/validation'
import outputSchema from 'schemas/validation/feature/outputSchema'

import { NumberInput } from 'components/patterns/Inputs'
import ScalingInput from './ScalingInput'
import {
  StyledSelectMenu,
  ItalicSpan,
  Divider,
  DivisionDiv,
  EquationDiv,
} from './PwmEditor.style'

const rule = schemaProp(outputSchema)

const {
  FIXED_DUTY_CYCLE_AND_FREQUENCY,
  VARIABLE_DUTY_CYCLE,
} = PWM_MODES_MAPPING

const pwmModeOptions = PWM_MODES.map((mode) => ({
  key: mode,
  value: mode,
  title: pwmModeTitle(mode),
}))

const PwmModeTooltip = () => {
  return (
    <EquationDiv>
      <ItalicSpan>{strings.pwm_mode_equation_begin}</ItalicSpan>
      <DivisionDiv>
        <ItalicSpan>{strings.pwm_mode_equation_dividend}</ItalicSpan>
        <Divider />
        <ItalicSpan>{strings.pwm_mode_equation_divisor}</ItalicSpan>
      </DivisionDiv>
    </EquationDiv>
  )
}

const PwmEditor = ({ id }) => {
  const dispatch = useDispatch()
  const setField = (field) => (value) =>
    dispatch(setOutputField(field, value, id))

  const {
    pwmMode,
    dutyCycle,
    dutyCycleMin,
    dutyCycleMax,
    frequency,
    frequencyMin,
    frequencyMax,
    scalingQuery,
  } = useSelector(getPwmOutputProps, { id })

  // update functions
  const updatePwmMode = setField('pwmMode')
  const updateFrequency = setField('frequency')
  const updateFrequencyMin = setField('frequencyMin')
  const updateFrequenceyMax = setField('frequencyMax')
  const updateDutyCycle = setField('dutyCycle')
  const updateDutyCycleMin = setField('dutyCycleMin')
  const updateDutyCycleMax = setField('dutyCycleMax')

  const modeEq = equals(pwmMode)

  const dutyCycleInput = (label, value, onChange, rules) => (
    <NumberInput
      className="form-field"
      key={label}
      vertical
      label={label}
      hint={strings.duty_cycle_hint}
      value={value}
      name={label}
      onChange={onChange}
      rules={rules}
    />
  )

  const frequencyInput = (label, value, onChange, rules) => (
    <NumberInput
      className="form-field"
      key={label}
      vertical
      label={label}
      hint={strings.frequency_hint}
      value={value}
      name={label}
      onChange={onChange}
      rules={rules}
    />
  )

  const fixedOutput = () => (
    <>
      {dutyCycleInput(
        strings.duty_cycle,
        dutyCycle,
        updateDutyCycle,
        rule('dutyCycle')
      )}
      {frequencyInput(
        strings.frequency,
        frequency,
        updateFrequency,
        rule('frequency')
      )}
    </>
  )

  const variableOutputs = () => {
    const outputs = () => {
      if (modeEq(VARIABLE_DUTY_CYCLE)) {
        return (
          <>
            {dutyCycleInput(
              strings.duty_cycle_min,
              dutyCycleMin,
              updateDutyCycleMin,
              rule('dutyCycleMin', { dutyCycleMax })
            )}
            {dutyCycleInput(
              strings.duty_cycle_max,
              dutyCycleMax,
              updateDutyCycleMax,
              rule('dutyCycleMax', { dutyCycleMin })
            )}
            {frequencyInput(
              strings.frequency,
              frequency,
              updateFrequency,
              rule('frequency')
            )}
          </>
        )
      }

      // VARIABLE_FREQUENCY
      return (
        <>
          {dutyCycleInput(
            strings.duty_cycle,
            dutyCycle,
            updateDutyCycle,
            rule('dutyCycle')
          )}
          {frequencyInput(
            strings.frequency_min,
            frequencyMin,
            updateFrequencyMin,
            rule('frequencyMin', { frequencyMax })
          )}
          {frequencyInput(
            strings.frequency_max,
            frequencyMax,
            updateFrequenceyMax,
            rule('frequencyMax', { frequencyMin })
          )}
        </>
      )
    }

    return (
      <>
        {outputs()}
        <ScalingInput uri={scalingQuery} outputId={id} />
      </>
    )
  }

  return (
    <>
      <StyledSelectMenu
        label={strings.pwm_mode}
        name={strings.pwm_mode}
        className="form-field"
        value={pwmMode}
        onChange={updatePwmMode}
        options={pwmModeOptions}
        mask={(title, value) => `${value}: ${title}`}
        rules={rule('pwmMode')}
        tooltipAutoWidth
        tooltip={pwmMode !== FIXED_DUTY_CYCLE_AND_FREQUENCY && PwmModeTooltip}
      />

      {modeEq(FIXED_DUTY_CYCLE_AND_FREQUENCY)
        ? fixedOutput()
        : variableOutputs()}
    </>
  )
}

PwmEditor.propTypes = {
  id: PropTypes.string,
}

export default PwmEditor
